import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import OpenRegisterModal from './OpenRegisterModal';
import CloseRegisterModal from './CloseRegisterModal';
import CashRegisterZohoPanel from './CashRegisterZohoPanel';
import '../../styles/CashRegister.css';

const BRANCHES = ['Pultney', 'Kissy'];

const formatAmount = (value) => {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

const formatSignature = (employee, portalUser) => {
  const employeeName = employee?.employee_name;
  const portalName = portalUser?.name;
  if (employeeName && portalName) {
    return `${employeeName} (${portalName})`;
  }
  return employeeName || portalName || '—';
};

const CashRegisterPage = () => {
  const { hasPermission } = useAuth();
  const [branch, setBranch] = useState('Pultney');
  const [status, setStatus] = useState({ isOpen: false, session: null });
  const [sessions, setSessions] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [error, setError] = useState(null);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [zohoCache, setZohoCache] = useState({});

  const canOpen = hasPermission('/cash-register/open', 'Execute');
  const canClose = hasPermission('/cash-register/close', 'Execute');
  const canReview = hasPermission('/cash-register/review', 'Execute');
  const canViewDiscrepancy = hasPermission('/cash-register/view-discrepancy', 'View');
  const canViewZoho = hasPermission('/cash-register/view-zoho-reconciliation', 'View');

  const colSpan =
    7 +
    (canViewDiscrepancy ? 1 : 0) +
    (canReview ? 1 : 0) +
    (canViewZoho ? 1 : 0);

  const fetchStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/cash-register/status`,
        { params: { branch } }
      );
      setStatus(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load register status.');
    } finally {
      setLoadingStatus(false);
    }
  }, [branch]);

  const fetchHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/cash-register`,
        { params: { branch, limit: 25 } }
      );
      setSessions(response.data.sessions || []);
    } catch (err) {
      console.error('Failed to load register history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [branch]);

  const fetchZohoReconciliation = useCallback(async (sessionId, forceRefresh = false) => {
    if (!canViewZoho) return;

    let shouldFetch = true;
    setZohoCache((prev) => {
      const existing = prev[sessionId];
      if (!forceRefresh && existing?.data && !existing?.error) {
        shouldFetch = false;
        return prev;
      }
      return {
        ...prev,
        [sessionId]: {
          ...existing,
          loading: true,
          error: null,
        },
      };
    });

    if (!shouldFetch) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/cash-register/${sessionId}/zoho-reconciliation`
      );
      setZohoCache((prev) => ({
        ...prev,
        [sessionId]: {
          data: response.data,
          loading: false,
          error: null,
        },
      }));
    } catch (err) {
      setZohoCache((prev) => ({
        ...prev,
        [sessionId]: {
          data: null,
          loading: false,
          error: err.response?.data?.error || 'Failed to load Zoho reconciliation data.',
        },
      }));
    }
  }, [canViewZoho]);

  useEffect(() => {
    fetchStatus();
    fetchHistory();
    setExpandedSessionId(null);
    setZohoCache({});
  }, [fetchStatus, fetchHistory]);

  const handleRefresh = () => {
    fetchStatus();
    fetchHistory();
  };

  const handleReview = async (sessionId) => {
    if (!window.confirm('Mark this register session as reviewed?')) return;
    try {
      setReviewingId(sessionId);
      await axios.patch(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/cash-register/${sessionId}/review`
      );
      handleRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to mark session as reviewed.');
    } finally {
      setReviewingId(null);
    }
  };

  const toggleExpand = (sessionId) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null);
      return;
    }
    setExpandedSessionId(sessionId);
    const cached = zohoCache[sessionId];
    if (!cached?.data && !cached?.loading) {
      fetchZohoReconciliation(sessionId);
    }
  };

  const openSession = status.session;

  return (
    <div className="cash-register-page">
      <h1>Cash Register</h1>

      <div className="cash-register-controls">
        <div className="form-field">
          <label htmlFor="branch-select">Branch</label>
          <select
            id="branch-select"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
          >
            {BRANCHES.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>

        <div className="cash-register-status-card">
          {loadingStatus ? (
            <div className="cash-register-loading">Loading status...</div>
          ) : error ? (
            <div className="cash-register-error">{error}</div>
          ) : (
            <>
              <span className={`status-badge ${status.isOpen ? 'open' : 'closed'}`}>
                {status.isOpen ? 'Open' : 'Closed'}
              </span>
              {status.isOpen && openSession && (
                <div className="status-details">
                  <p><strong>Opened by:</strong> {formatSignature(openSession.openedBy, openSession.openedByUser)}</p>
                  <p><strong>Opened at:</strong> {formatDateTime(openSession.opened_at)}</p>
                  <p><strong>Opening balance:</strong> {formatAmount(openSession.opening_balance)}</p>
                </div>
              )}
              {!status.isOpen && (
                <div className="status-details">
                  <p>No register is currently open for {branch}.</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="cash-register-actions">
          {!status.isOpen && canOpen && (
            <button
              type="button"
              className="btn-open-register"
              onClick={() => setShowOpenModal(true)}
              disabled={loadingStatus}
            >
              Open Register
            </button>
          )}
          {status.isOpen && canClose && openSession && (
            <button
              type="button"
              className="btn-close-register"
              onClick={() => setShowCloseModal(true)}
            >
              Close Register
            </button>
          )}
        </div>
      </div>

      <div className="cash-register-history">
        <h2>Register History — {branch}</h2>
        {loadingHistory ? (
          <div className="cash-register-loading">Loading history...</div>
        ) : sessions.length === 0 ? (
          <div className="cash-register-empty">No register sessions found for this branch.</div>
        ) : (
          <div className="cash-register-table-wrap">
            <table className="cash-register-table">
              <thead>
                <tr>
                  {canViewZoho && <th aria-label="Expand" />}
                  <th>Status</th>
                  <th>Opened</th>
                  <th>Opened By</th>
                  <th>Opening</th>
                  <th>Closed</th>
                  <th>Closed By</th>
                  <th>Closing</th>
                  {canViewDiscrepancy && <th>Discrepancy</th>}
                  {canReview && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => {
                  const hasDiscrepancy =
                    canViewDiscrepancy &&
                    session.opening_discrepancy != null &&
                    Math.abs(parseFloat(session.opening_discrepancy)) > 0.009;
                  const isExpanded = expandedSessionId === session.id;
                  const zohoState = zohoCache[session.id] || {};

                  return (
                    <React.Fragment key={session.id}>
                      <tr className={hasDiscrepancy ? 'discrepancy-row' : ''}>
                        {canViewZoho && (
                          <td>
                            <button
                              type="button"
                              className={`btn-expand-row ${isExpanded ? 'expanded' : ''}`}
                              onClick={() => toggleExpand(session.id)}
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? 'Collapse Zoho details' : 'Expand Zoho details'}
                            >
                              {isExpanded ? '▼' : '▶'}
                            </button>
                          </td>
                        )}
                        <td>
                          <span className={`session-status ${session.status}`}>
                            {session.status}
                          </span>
                        </td>
                        <td>{formatDateTime(session.opened_at)}</td>
                        <td>{formatSignature(session.openedBy, session.openedByUser)}</td>
                        <td>{formatAmount(session.opening_balance)}</td>
                        <td>{formatDateTime(session.closed_at)}</td>
                        <td>{formatSignature(session.closedBy, session.closedByUser)}</td>
                        <td>{formatAmount(session.closing_balance)}</td>
                        {canViewDiscrepancy && (
                          <td>
                            {session.opening_discrepancy != null ? (
                              hasDiscrepancy ? (
                                <span className="discrepancy-badge">
                                  {formatAmount(session.opening_discrepancy)} vs prior close ({formatAmount(session.prior_closing_balance)})
                                </span>
                              ) : (
                                'None'
                              )
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        {canReview && (
                          <td>
                            {session.status === 'closed' && (
                              <button
                                type="button"
                                className="btn-review"
                                onClick={() => handleReview(session.id)}
                                disabled={reviewingId === session.id}
                              >
                                {reviewingId === session.id ? '...' : 'Mark Reviewed'}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                      {canViewZoho && isExpanded && (
                        <tr className="cash-register-expanded-row">
                          <td colSpan={colSpan}>
                            <CashRegisterZohoPanel
                              session={session}
                              data={zohoState.data}
                              loading={zohoState.loading}
                              error={zohoState.error}
                              onRefresh={() => fetchZohoReconciliation(session.id, true)}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showOpenModal && (
        <OpenRegisterModal
          branch={branch}
          onClose={() => setShowOpenModal(false)}
          onSuccess={() => {
            setShowOpenModal(false);
            handleRefresh();
          }}
        />
      )}

      {showCloseModal && openSession && (
        <CloseRegisterModal
          session={openSession}
          onClose={() => setShowCloseModal(false)}
          onSuccess={() => {
            setShowCloseModal(false);
            handleRefresh();
          }}
        />
      )}
    </div>
  );
};

export default CashRegisterPage;
